import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import striptags from 'striptags';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import { useCachedMedia } from 'context';
import { formatters } from 'utils';
import Slider from 'rc-slider';
import { ErrorList } from 'components/common/inlines';
import QuillEditor from 'components/common/QuillEditor';
import MediaRelationField from 'components/views/CustomTitleBuilder/CustomTitleField/MediaRelationField';
import Color from 'components/common/DjangoFormField/Color';
import Select from 'components/common/Select';
import StructuredContentBuilder from 'components/views/StructuredContentBuilder';
import slideFormDefaults from './slideFormDefaults';
import RelatedItemsField from './RelatedItemsField';

export const nameSourceOptions = [
  { value: 'media_name', label: 'Use media name' },
  { value: 'custom', label: 'Use custom name' },
  { value: 'none', label: 'None' },
];

export const descriptionSourceOptions = [
  { value: 'media_long_desc', label: 'Use media long description' },
  { value: 'media_short_desc', label: 'Use media short description' },
  { value: 'custom', label: 'Use custom description' },
  { value: 'none', label: 'None' },
];

export const creditSourceOptions = [
  { value: 'media_credit', label: 'Use media credit' },
  { value: 'custom', label: 'Use custom credit' },
  { value: 'none', label: 'None' },
];

export const selectFieldOptions = {
  nameSource: nameSourceOptions,
  descriptionSource: descriptionSourceOptions,
  creditSource: creditSourceOptions,
};

const getSelectVal = (options, value) => options.find(opt => opt.value === value);

const fieldPrefix = 'slideshowBuilderForm-';

const Form = ({
  item,
  index,
  relatedItems,
  onChange,
  onRelatedItemsChange,
}) => {
  const containerRef = useRef();

  const [fields, setFields] = useState({ ...slideFormDefaults });
  const updateField = (name, val) => setFields(oldState => ({ ...oldState, [name]: val }));

  const mediaData = useCachedMedia(item.media);

  const initFormValues = () => {
    setFields(Object.entries(item).reduce((result, [key, val]) => {
      if (slideFormDefaults.hasOwnProperty(key)) {
        // Keep explicit false for checkbox fields, but convert undefined or null to empty strings
        result[key] = (val || val === false) ? val : '';
      }
      return result;
    }, {}));
  };

  useEffect(() => {
    initFormValues();
    containerRef.current.scrollTop = 0;
    return () => handleFormChange.flush();
  }, [item.itemId]);

  useEffect(() => {
    if (mediaData && !fields.slug) {
      updateField('slug', formatters.slug(mediaData.name));
    }
  }, [mediaData]);

  const handleFormChange = useCallback(debounce(fields => {
    onChange(Object.entries(fields).reduce((result, [key, val]) => {
      // Convert empty strings to null
      result[key] = val === '' ? null : val;
      return result;
    }, {}));
  }, 300), [onChange]);

  useEffect(() => {
    handleFormChange(fields);
  }, [JSON.stringify(fields)]);

  const handleInputChange = evt => {
    let { name, value, type, checked } = evt.target;
    name = name.replace(fieldPrefix, '');
    if (type === 'checkbox') {
      value = checked;
    } else if (name === 'slug') {
      value = formatters.slug(value);
    }
    updateField(name, value);
  };

  const createFieldHandler = name => val => updateField(name, val);

  const slideTypeVal = {
    bleed: '',
    'text-bleed': 'text',
    'text-fit': 'text',
    blocks: 'blocks',
  }[fields.layout] || '';

  const handleSlideTypeChange = evt => {
    const isBleed = ['bleed', 'text-bleed'].includes(fields.layout);
    let value = evt.target.value;
    if (value === 'text') {
      value = isBleed ? 'text-bleed' : 'text-fit';
    } else if (isBleed && value === '') {
      value = 'bleed';
    }
    updateField('layout', value);
  };

  const isSc = fields.layout === 'blocks';
  const isText = ['text-fit', 'text-bleed'].includes(fields.layout);

  const handleLayoutChange = evt => {
    const inputVal = evt.target.value;
    let value = inputVal;
    if (isText) value = `text-${inputVal || 'fit'}`;
    updateField('layout', value);
  };

  useEffect(() => {
    if (isText && mediaData && mediaData.type !== 'Image') {
      updateField('media', null);
    }
  }, [isText]);

  const handleStructuredContentChange = value => {
    if (!isEqual(fields.structuredContent, value)) {
      updateField('structuredContent', value);
    }
  };

  const errorClass = fieldName => item.errors[fieldName] ? 'has-error' : '';

  let nameInput = <input key="nameSource3" type="text" className="form-input" disabled />;
  switch (fields.nameSource) {
    case 'media_name':
      nameInput = <input key="nameSource1" type="text" className="form-input" value={mediaData ? mediaData.name : ''} disabled />;
      break;
    case 'custom':
      nameInput = (
        <input
          key="nameSource2"
          name={fieldPrefix + 'name'}
          type="text"
          className="form-input"
          value={fields.name}
          onChange={handleInputChange}
        />
      );
      break;
  }

  let descriptionInput = <textarea key="descriptionSource3" cols="30" rows="3" className="form-input" disabled />;
  switch (fields.descriptionSource) {
    case 'media_long_desc':
      descriptionInput = (
        <textarea
          key="descriptionSource1"
          cols="30"
          rows="3"
          className="form-input"
          value={mediaData ? striptags(mediaData.longDescription) : ''}
          disabled
        />
      );
      break;
    case 'media_short_desc':
      descriptionInput = (
        <textarea
          key="descriptionSource2"
          cols="30"
          rows="3"
          className="form-input"
          value={mediaData ? striptags(mediaData.shortDescription) : ''}
          disabled
        />
      );
      break;
    case 'custom':
      descriptionInput = (
        <QuillEditor
          inputName={fieldPrefix + 'description'}
          value={fields.description}
          onChange={createFieldHandler('description')}
          size="small"
        />
      );
      break;
  }

  let creditInput = <input key="creditSource3" type="text" className="form-input" disabled />;
  switch (fields.creditSource) {
    case 'media_credit':
      creditInput = (
        <textarea
          key="creditSource1"
          cols="30"
          rows="3"
          className="form-input"
          value={mediaData ? striptags(mediaData.credit) : ''}
          disabled
        />
      );
      break;
    case 'custom':
      creditInput = (
        <QuillEditor
          inputName={fieldPrefix + 'credit'}
          value={fields.credit}
          onChange={createFieldHandler('credit')}
          size="small"
        />
      );
      break;
  }

  let structuredContentErrors = {};
  if (item && item.errors && item.errors.structuredContent && item.errors.structuredContent.length > 0) {
    structuredContentErrors = JSON.parse(item.errors.structuredContent[0].message);
  }

  const otherErrors = item && Object.entries(item.errors).reduce((result, [key, value]) => {
    if (key !== 'structuredContent') {
      result[key] = value;
    }
    return result;
  }, {});
  const hasErrors = otherErrors && Object.keys(otherErrors).length > 0;

  return (
    <div className="title-builder-form-container" ref={containerRef}>
      <header className="title-builder-form-header">
        <div>
          <h5><span>Editing:</span> Slide {index}</h5>
        </div>
      </header>

      <div className="title-builder-form">
        {hasErrors && <ErrorList className="mb-5" errors={item.errors} />}

        <div className={classNames('form-group', 'mb-5', errorClass('layout'))} style={{ maxWidth: 200 }}>
          <label className="block-label">Slide Type</label>
          <select name="layout" className="form-select" value={slideTypeVal} onChange={handleSlideTypeChange}>
            <option value="">Media</option>
            <option value="text">Text</option>
            <option value="blocks">Blocks</option>
          </select>
        </div>

        {isSc ? (
          <>
            <div className="form-group mb-4">
              <label className="block-label">Content</label>
              <StructuredContentBuilder
                key={item.itemId}
                style={{ minHeight: 500 }}
                initialValue={fields.structuredContent}
                errors={structuredContentErrors}
                allowSubItemSource={false}
                allowConditionalDisplay={false}
                onChange={handleStructuredContentChange}
              />
            </div>

            <div className={classNames('form-group', 'mb-4', errorClass('description'))}>
              <label className="block-label">Description</label>
              <QuillEditor
                inputName={fieldPrefix + 'description'}
                value={fields.description}
                onChange={createFieldHandler('description')}
                size="small"
              />
            </div>
          </>
        ) : (
          <>
            <div className={classNames('form-group', 'mb-5', errorClass('media'))}>
              <label className="block-label">Media</label>
              <p className="form-input-hint m-0">
                {isText
                  ? 'Use the form below to select a background image for this slide.'
                  : 'Use the form below to select an image or video for this slide.'}
              </p>
              <MediaRelationField
                limitTypes={isText ? ['image'] : ['image', 'video']}
                value={fields.media}
                onChange={createFieldHandler('media')}
              />
            </div>

            <div className={classNames('form-group', 'mb-5', errorClass('layout'))}>
              <label className="block-label">Image Display</label>
              <label className="form-radio m-0">
                <input
                  type="radio"
                  value=""
                  checked={['text-fit', ''].includes(fields.layout)}
                  onChange={handleLayoutChange}
                />
                <i className="form-icon" /> Fit <span className="form-input-hint">– {isText ? 'Background image' : 'Image'} fits within the slide area, preserving original crop.</span>
              </label>
              <label className="form-radio m-0">
                <input
                  type="radio"
                  value="bleed"
                  checked={['text-bleed', 'bleed'].includes(fields.layout)}
                  onChange={handleLayoutChange}
                />
                <i className="form-icon" /> Full Bleed <span className="form-input-hint">– {isText ? 'Background image' : 'Image'} extends to cover the slide area. Some cropping may occur.</span>
              </label>
            </div>

            {isText && (
              <>
                <div className={classNames('form-group', 'mb-4', errorClass('text'))}>
                  <label className="block-label">Text</label>
                  <p className="form-input-hint m-0 mb-1">This text will appear overlaid on the slide.</p>
                  <QuillEditor
                    inputName={fieldPrefix + 'text'}
                    value={fields.text}
                    onChange={createFieldHandler('text')}
                    size="large"
                    useFullMenu
                  />
                </div>

                <div className="columns">
                  <div className="column col-3">
                    <div className={classNames('form-group', 'mb-4', errorClass('textColor'))}>
                      <label className="block-label">Text Color</label>
                      <Color
                        name={fieldPrefix + 'textColor'}
                        value={fields.textColor}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="column col-3">
                    <div className={classNames('form-group', 'mb-4', errorClass('overlayColor'))}>
                      <label className="block-label">Overlay Color</label>
                      <Color
                        name={fieldPrefix + 'overlayColor'}
                        value={fields.overlayColor}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="column col-6">
                    <div className={classNames('form-group', 'mb-4', errorClass('overlayOpacity'))}>
                      <label className="block-label">Overlay Opacity</label>
                      <div className="d-flex align-items-center">
                        <div className="slider-field" style={{ flex: 1 }}>
                          <Slider
                            value={fields.overlayOpacity}
                            onChange={createFieldHandler('overlayOpacity')}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="input-group">
                            <input
                              name={fieldPrefix + 'overlayOpacity'}
                              type="text"
                              className="form-input input-sm"
                              style={{ width: 35 }}
                              value={fields.overlayOpacity}
                              onChange={handleInputChange}
                            />
                            <span className="input-group-addon addon-sm">%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="mt-0 mb-3" />
              </>
            )}

            {[
              ['name', 'Name', nameInput, 'nameSource'],
              ['description', 'Description', descriptionInput, 'descriptionSource'],
              ['credit', 'Credit', creditInput, 'creditSource'],
            ].map(([fieldName, label, input, sourceFieldName]) => (
              <div key={fieldName} className={classNames('form-group', 'mb-3', errorClass(fieldName), errorClass(sourceFieldName))}>
                <label className="block-label">{label}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  <div style={{ flex: '0 0 240px', margin: '0 10px 10px 0' }}>
                    <Select
                      value={getSelectVal(selectFieldOptions[sourceFieldName], fields[sourceFieldName])}
                      options={selectFieldOptions[sourceFieldName]}
                      onChange={createFieldHandler(sourceFieldName)}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 300 }}>
                    {input}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        <div className={classNames('form-group', 'mb-3', errorClass('slug'))}>
          <label className="block-label">URL Slug</label>
          <input
            key="slug"
            name={fieldPrefix + 'slug'}
            type="text"
            className="form-input"
            value={fields.slug}
            onChange={handleInputChange}
          />
          <p className="form-input-hint m-0 mt-1">This text will appear in the URL and must contain only lowercase letters, numbers, and hyphens.</p>
        </div>

        <hr className="mt-0 mb-3" />

        <RelatedItemsField
          name="relatedItems"
          label="Related Content"
          helpText="Use the form below to add related links or media to this slide."
          value={relatedItems}
          onChange={onRelatedItemsChange}
        />
      </div>
    </div>
  );
};

Form.propTypes = {
  item: PropTypes.object,
  index: PropTypes.number,
  relatedItems: PropTypes.array,
  onChange: PropTypes.func,
  onRelatedItemsChange: PropTypes.func,
};

export default Form;
